import { Link } from "react-router-dom"
import { SiteHeader } from "@/components/site-header"
import { GitBranchIcon } from "lucide-react"

export default function BateaMultiLegPage() {
  return (
    <>
      <SiteHeader title="Envíos Multi-pierna — Batea" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-5xl space-y-10">

          {/* ── Qué es ── */}
          <section>
            <h2 className="text-base font-semibold mb-2">¿Qué es un envío multi-pierna?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Un envío <strong>multi-pierna</strong> (
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">is_multi_leg = true</code>)
              es exclusivo de la línea de trabajo <strong>Batea</strong> y representa
              un traslado que requiere dos choferes en secuencia: el Chofer A lleva la carga
              desde el origen hasta un punto de transferencia (Chivilcoy, por ejemplo) y el
              Chofer B la continúa hasta el destino final. El sistema coordina la transición
              automáticamente cuando el Chofer A registra la salida de origen.
            </p>
          </section>

          {/* ── Cómo activarlo ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Cómo activarlo desde la web</h2>
            <ol className="space-y-3 text-sm text-muted-foreground list-none">
              {[
                <>Abrí el detalle del envío en <code className="text-xs bg-muted px-1.5 py-0.5 rounded">/shipments/:id</code> y entrá en modo edición.</>,
                <>En el bloque <strong>Viajes</strong>, activá el toggle <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-700 px-2 py-0.5 text-[11px] font-medium"><GitBranchIcon className="size-3" />Multi-pierna</span>. Solo aparece cuando el envío tiene <code className="text-xs bg-muted px-1.5 py-0.5 rounded">workLine = batea</code>.</>,
                <>Guardá. El campo <code className="text-xs bg-muted px-1.5 py-0.5 rounded">is_multi_leg</code> se actualiza en la DB.</>,
                <>Asegurate de que el envío tenga exactamente <strong>2 viajes</strong> asignados:<br/>
                  — <strong>Pierna 1</strong>: el primer viaje creado, asignado al Chofer A (origen → transferencia).<br/>
                  — <strong>Pierna 2</strong>: el segundo viaje, asignado al Chofer B (transferencia → destino).</>,
              ].map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="shrink-0 flex items-center justify-center size-5 rounded-full bg-muted text-muted-foreground text-[11px] font-semibold mt-0.5">
                    {i + 1}
                  </span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* ── Transición automática ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Transición automática al registrar salida de origen</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Cuando el Chofer A toca <strong>Comenzar viaje</strong> en la app mobile (lo que
              escribe <code className="text-xs bg-muted px-1.5 py-0.5 rounded">shipments.origin_departure_at</code>),
              el servidor detecta que <code className="text-xs bg-muted px-1.5 py-0.5 rounded">is_multi_leg = true</code>{" "}
              y ejecuta la siguiente lógica <strong>en la misma request</strong>:
            </p>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-8">#</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Acción del servidor</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Campo afectado</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">1</td>
                    <td className="px-4 py-3">Verifica que el shipment tenga exactamente 2 viajes activos</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">trips WHERE shipment_id = ? AND deleted_at IS NULL</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">2</td>
                    <td className="px-4 py-3">Confirma que el chofer que hace la request es el dueño de la Pierna 1</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">trips[0].driver_id = acting_driver.id</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">3</td>
                    <td className="px-4 py-3">Completa la Pierna 1 del Chofer A</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">trips[0]: status = completado, arrival_at = now()</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">4</td>
                    <td className="px-4 py-3">Activa la Pierna 2 del Chofer B</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">trips[1]: status = en_curso, departure_at = now()</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              La app siempre llama a{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">PATCH /trips/:id</code>{" "}
              con <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{"{ status: 'en_curso' }"}</code>{" "}
              después de registrar la salida de origen. En un multi-leg de exactamente 2 piernas,
              el servidor ya marcó el trip de la Pierna 1 como{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">completado</code> — si recibe
              esa llamada, la ignora silenciosamente retornando{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{"{ id }"}</code>{" "}
              sin escribir nada. Esto garantiza que el trip de la Pierna 1 no sea pisado por la app.
            </p>
          </section>

          {/* ── Vista en la app mobile ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Vista en la app mobile del chofer</h2>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Chofer</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Antes de la transición</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Después de la transición</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 font-medium">Chofer A (Pierna 1)</td>
                    <td className="px-4 py-3 text-muted-foreground">Ve su viaje en <code className="text-xs bg-muted px-1 py-0.5 rounded">pendiente</code>, avanza hitos normalmente</td>
                    <td className="px-4 py-3 text-muted-foreground">Su viaje aparece como <code className="text-xs bg-muted px-1 py-0.5 rounded">completado</code> o desaparece del feed activo</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Chofer B (Pierna 2)</td>
                    <td className="px-4 py-3 text-muted-foreground">Su viaje está en <code className="text-xs bg-muted px-1 py-0.5 rounded">pendiente</code>, no aparece en el feed activo</td>
                    <td className="px-4 py-3 text-muted-foreground">Su viaje pasa a <code className="text-xs bg-muted px-1 py-0.5 rounded">en_curso</code> y aparece en el feed</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Visibilidad por estado del shipment ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Visibilidad del viaje por estado del shipment</h2>
            <div className="rounded-lg border overflow-hidden text-sm mb-3">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Estado shipment</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Chofer Pierna 1</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Chofer Pierna 2</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">planificado</td>
                    <td className="px-4 py-3 text-muted-foreground">✅ Ve el viaje, puede confirmar (<code className="text-xs bg-muted px-1 py-0.5 rounded">planificado → activo</code>)</td>
                    <td className="px-4 py-3 text-muted-foreground">❌ Oculto — no ve nada</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">activo</td>
                    <td className="px-4 py-3 text-muted-foreground">✅ Ve el viaje, ejecuta hitos 1-3</td>
                    <td className="px-4 py-3 text-muted-foreground">❌ Oculto hasta que Pierna 1 registre <code className="text-xs bg-muted px-1 py-0.5 rounded">originDepartureAt</code></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">Post-transición (<code className="text-xs bg-muted px-1 py-0.5 rounded">originDepartureAt</code> registrado)</td>
                    <td className="px-4 py-3 text-muted-foreground">Su viaje pasa a <code className="text-xs bg-muted px-1 py-0.5 rounded">completado</code> automáticamente</td>
                    <td className="px-4 py-3 text-muted-foreground">✅ Su viaje pasa a <code className="text-xs bg-muted px-1 py-0.5 rounded">en_curso</code>, ejecuta hitos 4-6</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">finalizado</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">completado</td>
                    <td className="px-4 py-3 text-muted-foreground"><code className="text-xs bg-muted px-1 py-0.5 rounded">completado</code> (el servidor completa todos los trips activos)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              La visibilidad es controlada por el servidor en{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">GET /trips/me</code>{" "}
              — la app no necesita cambios para implementar estas reglas.
            </p>
          </section>

          {/* ── Restricciones ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Restricciones y consideraciones</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {[
                "Solo aplica a shipments con workLine = 'batea'. El flag is_multi_leg puede ser true en la DB para cualquier shipment, pero la lógica de visibilidad, confirmación, transición automática y finalización en cascada solo se activa cuando work_line = 'batea'. El toggle tampoco aparece en la UI para nacionales ni internacionales.",
                "El servidor solo ejecuta la transición si el shipment tiene exactamente 2 viajes activos. Con 1 o más de 2, la lógica no se dispara (safety check).",
                "Los viajes se ordenan por created_at ASC para determinar cuál es Pierna 1 y cuál Pierna 2. Creá siempre el viaje del Chofer A primero.",
                "La transición es irreversible: una vez que Pierna 1 queda completado y Pierna 2 pasa a en_curso, no hay endpoint para revertirlo automáticamente.",
                "Si el Chofer A no es el dueño de la Pierna 1 (trips[0].driver_id ≠ acting_driver), la transición no se ejecuta — se loguea el caso pero el shipment igual guarda originDepartureAt.",
              ].map((item, i) => (
                <li key={i} className="flex gap-2">
                  <span className="shrink-0 text-muted-foreground/50 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* ── Archivos involucrados ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Archivos involucrados</h2>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Archivo</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Responsabilidad</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {[
                    ["apps/api/src/db/schema.ts", "Columna is_multi_leg boolean NOT NULL DEFAULT false en shipments"],
                    ["apps/api/src/routes/trips.ts", "GET /trips/me — dos filtros SQL batea-only. PATCH /trips/:id — guard que ignora silenciosamente status: en_curso sobre trips ya completado en shipments batea multi-leg"],
                    ["apps/api/src/routes/shipments/index.ts", "PATCH /shipments/:id — tres comportamientos batea-only: guard de confirmación (solo Pierna 1), transición automática al registrar originDepartureAt, finalización en cascada al pasar a finalizado"],
                    ["apps/driver/lib/api.ts", "Interfaz DriverTrip — campo isMultiLeg: boolean"],
                    ["apps/driver/hooks/useBateaHandlers.ts", "Siempre llama updateTripStatus después de startBateaShipment — el server ignora silenciosamente la llamada si el trip ya está completado (multi-leg guard en PATCH /trips/:id)"],
                    ["apps/web/src/pages/shipments/ShipmentDetailPage.tsx", "Toggle y badge Multi-pierna en el bloque Viajes (solo batea)"],
                    ["apps/web/src/pages/shipments/ShipmentDetailTypes.ts", "Campo isMultiLeg en ShipmentDetail y EMPTY_FORM"],
                    ["apps/web/src/pages/shipments/ShipmentDetailUtils.ts", "buildPayload incluye isMultiLeg"],
                    ["apps/api/src/routes/mobile.test.ts", "Contract tests: isMultiLeg en driverTripSchema"],
                    ["scripts/test-multileg-e2e.sh", "Script e2e: login dos choferes, hitos, verificación de transición"],
                  ].map(([file, desc]) => (
                    <tr key={file}>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground align-top w-1/2">{file}</td>
                      <td className="px-4 py-3 text-muted-foreground align-top">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Tests ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Tests</h2>
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3 text-sm">
              <div>
                <p className="font-medium mb-1">Unitarios (sin DB)</p>
                <code className="block text-xs bg-muted px-3 py-2 rounded">
                  bun test apps/api/src/routes/mobile.test.ts
                </code>
                <p className="text-xs text-muted-foreground mt-1">
                  Cubre: <code className="bg-muted px-1 py-0.5 rounded">isMultiLeg: true/false</code> en el schema de{" "}
                  <code className="bg-muted px-1 py-0.5 rounded">DriverTrip</code>, campo requerido (no nullable), rechazo de string.
                </p>
              </div>
              <div>
                <p className="font-medium mb-1">Contrato mobile (schema)</p>
                <code className="block text-xs bg-muted px-3 py-2 rounded">
                  bun test apps/api/src/routes/mobile.test.ts
                </code>
                <p className="text-xs text-muted-foreground mt-1">
                  Cubre las 4 combinaciones de <code className="bg-muted px-1 py-0.5 rounded">isMultiLeg</code> ×{" "}
                  <code className="bg-muted px-1 py-0.5 rounded">workLine</code> en el schema{" "}
                  <code className="bg-muted px-1 py-0.5 rounded">DriverTrip</code>. Valida que la restricción batea-only
                  es server-side y no rompe el contrato del schema mobile.
                </p>
              </div>
              <div>
                <p className="font-medium mb-1">E2E (requiere API + DB + datos)</p>
                <code className="block text-xs bg-muted px-3 py-2 rounded">
                  bash scripts/test-multileg-e2e.sh
                </code>
                <p className="text-xs text-muted-foreground mt-1">
                  Pre-condición: crear desde la UI un envío batea con <code className="bg-muted px-1 py-0.5 rounded">isMultiLeg = true</code>{" "}
                  y exactamente 2 viajes — Pierna 1 asignada al chofer de prueba{" "}
                  <code className="bg-muted px-1 py-0.5 rounded">DNI 99000001</code> y Pierna 2 al{" "}
                  <code className="bg-muted px-1 py-0.5 rounded">DNI 99000002</code>.
                </p>
              </div>
            </div>
          </section>

          {/* ── Ver también ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Ver también</h2>
            <div className="flex flex-wrap gap-2">
              {[
                { label: "App Mobile — Flujo Batea", href: "/docs/batea-mobile" },
                { label: "Estados de Envíos y Viajes", href: "/docs/estados-envios-viajes" },
                { label: "Acciones sobre viajes", href: "/docs/acciones-viajes" },
              ].map(({ label, href }) => (
                <Link
                  key={href}
                  to={href}
                  className="text-xs rounded-md border px-3 py-1.5 text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
                >
                  {label}
                </Link>
              ))}
            </div>
          </section>

        </div>
      </div>
    </>
  )
}
